// FireEngine: physically-grounded wildfire spread on a grid.
//
// Improvements over the original probabilistic cellular automaton:
//  * Fire-arrival accumulation — each burning cell pushes spread "progress"
//    into neighbors at a physical rate of spread (m/h); a neighbor ignites
//    when accumulated arrival reaches its center. Spread speed is therefore
//    tied to real ROS instead of per-step coin flips.
//  * Rothermel-style wind & slope factors (exponential wind gain along the
//    spread direction, tan²(slope) upslope gain, damped downslope).
//  * Fuel moisture derived from live weather (humidity, temperature, and a
//    48-hour precipitation memory) with a moisture-of-extinction cutoff.
//  * Multi-hour burn residence per cell scaled by fuel load; structures burn
//    longer than vegetation.
//  * Downwind ember spotting with exponentially-distributed distances —
//    replaces the special-cased "water jump" and can cross any barrier.
//  * Adaptive substeps so fast wind-driven runs are not capped at one
//    cell per simulation step.
//  * Lagged dead-fuel moisture — fuels dry out and re-wet with a ~2 h time
//    constant instead of instantly tracking humidity (realistic diurnal lag).
//  * Fire-intensity coupling — a cell's outward spread pressure declines as
//    its fuel is consumed, so old fire edges push weaker than fresh ones.
//  * Ignition-progress decay — accumulated preheat dissipates once the fire
//    front moves on, preventing unrealistic delayed creep ignitions.
//  * Stochastic wind-direction wobble (AR(1)) for organic fire shapes.
//
// All tool/drone semantics (water, retardant, dig, backfire) are preserved.

import {
  STATE_EMPTY,
  STATE_TREE,
  STATE_BURNING,
  STATE_WATER,
  STATE_INFRASTRUCTURE,
  STATE_SMOLDERING,
  STATE_INFRASTRUCTURE_BURNT,
  STATE_RETARDANT,
  STATE_DIGGING,
} from "./constants";

// --- Tunable model parameters -------------------------------------------
const BASE_ROS_M_PER_HR = 55; // no-wind, flat, bone-dry surface spread
const MIDFLAME_WIND_FACTOR = 0.45; // 10m wind -> midflame wind
const GUST_BLEND = 0.3; // how much gusts contribute to effective wind
const WIND_GAIN_COEFF = 0.22; // head-fire gain: phi_w = 1 + C * effWind^E
const WIND_GAIN_EXP = 1.35;
const MAX_WIND_PHI = 40;
const CROSSWIND_ROS_RATIO = 1.0; // flank ROS ≈ this × no-wind ROS under wind
const UPWIND_DAMP = 1.5; // extra exponential damping against the wind
const SLOPE_GAIN_COEFF = 5.275; // Rothermel: phi_s = C * tan(slope)^2
const MAX_SLOPE_PHI = 6;
const MOISTURE_OF_EXTINCTION = 0.25; // chaparral-like
const RETARDANT_SPREAD_MULT = 0.08;
const INFRA_SPREAD_MULT = 0.4; // structures ignite more slowly
const SMOLDER_SOURCE_MULT = 0.25;
const SPOT_MIN_WIND_MPH = 18;
const SPOT_MEAN_DIST_PER_MPH = 35; // meters of mean spotting per mph
const SPOT_MAX_DIST_M = 2500;
const RETARDANT_DURATION_HRS = 48;
const DIG_DURATION_HRS = 24;
const MAX_SUBSTEPS = 8;
const MOISTURE_LAG_HRS = 2; // dead-fuel moisture response time constant
const PROGRESS_DECAY_PER_HR = 0.06; // preheat dissipation when front moves on
const WIND_WOBBLE_DEG = 14; // stochastic direction wander amplitude

export const TOOL_EFFECT_RADIUS = {
  WATER_DROP: 2,
  RETARDANT_DROP: 3, // animation radius; actual effect is a line
  BACKFIRE: 1,
  DIG_BREAK: 1,
};
export const RETARDANT_LINE_LENGTH = 4;

const NEIGHBORS = [
  [-1, -1], [-1, 0], [-1, 1],
  [0, -1],           [0, 1],
  [1, -1],  [1, 0],  [1, 1],
];

const key = (r, c) => `${r},${c}`;

// Fine dead fuel moisture (fraction) from weather.
export const fuelMoisture = (weather) => {
  const rh = Math.max(0, Math.min(100, weather.humidity ?? 40));
  let m = 0.02 + 0.2 * Math.pow(rh / 100, 1.6);
  // Slightly drier when hot
  if (weather.tempC > 25) m -= Math.min(0.01, (weather.tempC - 25) * 0.0006);
  // Rain memory: recent precipitation wets fuels for ~48h
  m += Math.min(0.2, (weather.rain48 ?? 0) * 0.012);
  return Math.max(0.015, m);
};

const moistureDamping = (m) => {
  const x = 1 - m / MOISTURE_OF_EXTINCTION;
  return x <= 0 ? 0 : x * x;
};

export class FireEngine {
  constructor(scenario, opts = {}) {
    const { rows, cols } = scenario.dims;
    this.rows = rows;
    this.cols = cols;
    this.cellSizeM = scenario.cellSizeM;
    // Immutable shared references
    this.elevation = scenario.elevation;
    this.fuel = scenario.fuel;
    this.initialGridType = scenario.initialGridType;
    this.structureValue = scenario.structureValue;
    this.initialInfraCount = scenario.initialInfraCount;
    this.totalValue = scenario.totalValue;
    this.rng = opts.rng || Math.random;

    if (opts._cloneFrom) {
      const src = opts._cloneFrom;
      this.state = src.state.map((row) => Int8Array.from(row));
      this.progress = src.progress.map((row) => Float32Array.from(row));
      this.burnRemaining = src.burnRemaining.map((row) => Float32Array.from(row));
      this.smolderRemaining = src.smolderRemaining.map((row) =>
        Float32Array.from(row)
      );
      this.retardant = new Map(src.retardant);
      this.digging = new Map(src.digging);
      this.markers = { ...src.markers };
      this.moisture = src.moisture;
      this.windWobbleDeg = src.windWobbleDeg;
    } else {
      this.state = scenario.initialState.map((row) => Int8Array.from(row));
      this.progress = Array(rows)
        .fill(null)
        .map(() => new Float32Array(cols));
      this.burnRemaining = Array(rows)
        .fill(null)
        .map(() => new Float32Array(cols));
      this.smolderRemaining = Array(rows)
        .fill(null)
        .map(() => new Float32Array(cols));
      this.retardant = new Map(); // key -> hours remaining
      this.digging = new Map(); // key -> hours remaining
      this.markers = {}; // key -> 'RETARDANT_DROP' | 'DIG_BREAK'
      this.moisture = null; // lagged dead-fuel moisture; seeded on first step
      this.windWobbleDeg = 0; // AR(1) wind direction wander
      // Seed initial fires with a residence time
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (this.state[r][c] === STATE_BURNING) {
            this.burnRemaining[r][c] = this.residenceHours(r, c);
          }
        }
      }
    }
  }

  clone(rng) {
    return new FireEngine(
      {
        dims: { rows: this.rows, cols: this.cols },
        cellSizeM: this.cellSizeM,
        elevation: this.elevation,
        fuel: this.fuel,
        initialGridType: this.initialGridType,
        structureValue: this.structureValue,
        initialInfraCount: this.initialInfraCount,
        totalValue: this.totalValue,
      },
      { rng: rng || this.rng, _cloneFrom: this }
    );
  }

  inBounds(r, c) {
    return r >= 0 && r < this.rows && c >= 0 && c < this.cols;
  }

  residenceHours(r, c) {
    if (this.initialGridType[r][c] === STATE_INFRASTRUCTURE) return 5;
    return 0.75 + 2.5 * (this.fuel[r][c] || 0.5);
  }

  isCombustible(r, c) {
    const s = this.state[r][c];
    return (
      s === STATE_TREE || s === STATE_INFRASTRUCTURE || s === STATE_RETARDANT
    );
  }

  // Rate of spread (m/h) from source cell toward target cell.
  spreadRate(sr, sc, tr, tc, wind, damping) {
    const targetState = this.state[tr][tc];
    let mult = 1;
    if (targetState === STATE_RETARDANT) mult *= RETARDANT_SPREAD_MULT;
    if (this.initialGridType[tr][tc] === STATE_INFRASTRUCTURE)
      mult *= INFRA_SPREAD_MULT;

    const fuelFactor = 0.4 + 0.6 * (this.fuel[tr][tc] || 0.5);

    // Wind: elliptical fire shape. The head-fire gain (phi_head) applies at
    // full strength only downwind; an eccentricity factor derived from it
    // suppresses flanking spread to ~no-wind rates and chokes upwind spread.
    // +dc = east, +dr = south (row 0 is north).
    const dr = tr - sr;
    const dc = tc - sc;
    const dist = Math.hypot(dr, dc);
    const towardRad = ((wind.direction + 180) % 360) * (Math.PI / 180);
    const wx = Math.sin(towardRad); // east component
    const wy = -Math.cos(towardRad); // south component (grid +r)
    const cosTheta = (dc / dist) * wx + (dr / dist) * wy; // -1..1 vs wind
    const effWind =
      MIDFLAME_WIND_FACTOR *
      ((1 - GUST_BLEND) * wind.speed + GUST_BLEND * (wind.gusts ?? wind.speed));
    const phiHead = Math.min(
      MAX_WIND_PHI,
      1 + WIND_GAIN_COEFF * Math.pow(Math.max(0, effWind), WIND_GAIN_EXP)
    );
    // Eccentricity chosen so flank ROS ≈ CROSSWIND_ROS_RATIO × no-wind ROS
    const e = Math.max(0, 1 - CROSSWIND_ROS_RATIO / phiHead);
    let phiW = (phiHead * (1 - e)) / (1 - e * cosTheta);
    if (cosTheta < 0) phiW *= Math.exp(UPWIND_DAMP * cosTheta);
    phiW = Math.max(0.02, phiW);

    // Slope along the spread direction
    const dElev = this.elevation[tr][tc] - this.elevation[sr][sc];
    const runM = dist * this.cellSizeM;
    const tanSlope = dElev / runM;
    let phiS;
    if (tanSlope >= 0) {
      phiS = Math.min(MAX_SLOPE_PHI, 1 + SLOPE_GAIN_COEFF * tanSlope * tanSlope);
    } else {
      phiS = Math.max(0.35, 1 / (1 + 3 * Math.abs(tanSlope)));
    }

    return BASE_ROS_M_PER_HR * fuelFactor * damping * phiW * phiS * mult;
  }

  igniteCell(r, c) {
    this.state[r][c] = STATE_BURNING;
    this.burnRemaining[r][c] = this.residenceHours(r, c);
    this.progress[r][c] = 0;
    this.retardant.delete(key(r, c));
    this.digging.delete(key(r, c));
  }

  // Advance the fire by dtHours under the given weather.
  step(weather, dtHours) {
    // Dead-fuel moisture lags atmospheric conditions (~2 h time constant):
    // fuels stay dry into the evening and re-wet slowly after rain/humidity.
    const targetMoisture = fuelMoisture(weather);
    if (this.moisture === null) this.moisture = targetMoisture;
    const alpha = 1 - Math.exp(-dtHours / MOISTURE_LAG_HRS);
    this.moisture += (targetMoisture - this.moisture) * alpha;
    const damping = moistureDamping(this.moisture);

    // Adaptive substepping so fast runs can cross >1 cell per step
    const maxRos =
      BASE_ROS_M_PER_HR *
      damping *
      Math.min(
        MAX_WIND_PHI,
        1 +
          WIND_GAIN_COEFF *
            Math.pow(MIDFLAME_WIND_FACTOR * (weather.gusts ?? weather.speed), WIND_GAIN_EXP)
      );
    const nSub = Math.max(
      1,
      Math.min(MAX_SUBSTEPS, Math.ceil((maxRos * dtHours) / this.cellSizeM))
    );
    const dt = dtHours / nSub;

    for (let s = 0; s < nSub; s++) {
      this.substep(weather, dt, damping);
    }

    // Timed effects tick on the full step
    for (const [k, hrs] of this.digging) {
      const left = hrs - dtHours;
      if (left <= 0) {
        this.digging.delete(k);
        const [r, c] = k.split(",").map(Number);
        if (this.state[r][c] === STATE_DIGGING) this.state[r][c] = STATE_EMPTY;
      } else {
        this.digging.set(k, left);
      }
    }
    for (const [k, hrs] of this.retardant) {
      const left = hrs - dtHours;
      if (left <= 0) {
        this.retardant.delete(k);
        const [r, c] = k.split(",").map(Number);
        if (this.state[r][c] === STATE_RETARDANT) {
          const init = this.initialGridType[r][c];
          this.state[r][c] =
            init === STATE_TREE || init === STATE_INFRASTRUCTURE
              ? init
              : STATE_EMPTY;
        }
      } else {
        this.retardant.set(k, left);
      }
    }
  }

  substep(weather, dt, damping) {
    const { rows, cols, rng } = this;
    const ignitions = [];

    // Wind direction wanders stochastically (AR(1)) so fire shapes stay
    // organic instead of perfectly elliptical.
    this.windWobbleDeg =
      this.windWobbleDeg * 0.72 + (rng() - 0.5) * WIND_WOBBLE_DEG;
    const effWeather = {
      ...weather,
      direction: ((weather.direction ?? 0) + this.windWobbleDeg + 360) % 360,
    };

    for (let r = 0; r < rows; r++) {
      const stateRow = this.state[r];
      for (let c = 0; c < cols; c++) {
        const s = stateRow[c];
        if (s !== STATE_BURNING && s !== STATE_SMOLDERING) continue;
        let sourceMult = s === STATE_BURNING ? 1 : SMOLDER_SOURCE_MULT;
        // Intensity coupling: as a burning cell consumes its fuel, the heat
        // it pushes outward declines.
        if (s === STATE_BURNING) {
          const intensity = Math.max(
            0.3,
            Math.min(1, this.burnRemaining[r][c] / this.residenceHours(r, c))
          );
          sourceMult *= 0.35 + 0.65 * intensity;
        }

        // Push arrival progress into combustible neighbors
        for (const [dr, dc] of NEIGHBORS) {
          const nr = r + dr;
          const nc = c + dc;
          if (!this.inBounds(nr, nc) || !this.isCombustible(nr, nc)) continue;
          const ros = this.spreadRate(r, c, nr, nc, effWeather, damping);
          if (ros <= 0) continue;
          const distM = Math.hypot(dr, dc) * this.cellSizeM;
          const jitter = 0.6 + 0.8 * rng();
          this.progress[nr][nc] += ((ros * sourceMult * dt) / distM) * jitter;
          if (this.progress[nr][nc] >= 1) ignitions.push(nr, nc);
        }

        // Ember spotting downwind
        if (s === STATE_BURNING) {
          const effWind =
            (1 - GUST_BLEND) * effWeather.speed +
            GUST_BLEND * (effWeather.gusts ?? effWeather.speed);
          if (effWind >= SPOT_MIN_WIND_MPH && damping > 0.05) {
            const prob =
              Math.min(0.12, 0.004 * Math.pow(effWind / SPOT_MIN_WIND_MPH, 2.2)) *
              damping *
              dt;
            if (rng() < prob) {
              const meanDist = Math.min(
                SPOT_MAX_DIST_M,
                effWind * SPOT_MEAN_DIST_PER_MPH
              );
              const distM = Math.min(
                SPOT_MAX_DIST_M,
                -Math.log(1 - rng()) * meanDist
              );
              const towardRad =
                (((effWeather.direction + 180) % 360) + (rng() - 0.5) * 24) *
                (Math.PI / 180);
              // grid: +r = south = -cos(toward); +c = east = sin(toward)
              const cells = distM / this.cellSizeM;
              const trr = Math.round(r - Math.cos(towardRad) * cells);
              const tcc = Math.round(c + Math.sin(towardRad) * cells);
              if (this.inBounds(trr, tcc) && this.isCombustible(trr, tcc)) {
                // Embers only take hold in receptive (dry) fuels
                let chunk = (0.6 + 0.6 * rng()) * (0.4 + 0.6 * damping);
                if (this.state[trr][tcc] === STATE_RETARDANT)
                  chunk *= RETARDANT_SPREAD_MULT;
                this.progress[trr][tcc] += chunk;
                if (this.progress[trr][tcc] >= 1) ignitions.push(trr, tcc);
              }
            }
          }
        }
      }
    }

    // Burn-down: consume residence time, then smolder, then burn out
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const s = this.state[r][c];
        if (s === STATE_BURNING) {
          this.burnRemaining[r][c] -= dt;
          if (this.burnRemaining[r][c] <= 0) {
            this.state[r][c] = STATE_SMOLDERING;
            this.smolderRemaining[r][c] =
              this.initialGridType[r][c] === STATE_INFRASTRUCTURE ? 3 : 2;
          }
        } else if (s === STATE_SMOLDERING) {
          this.smolderRemaining[r][c] -= dt;
          if (this.smolderRemaining[r][c] <= 0) {
            this.state[r][c] =
              this.initialGridType[r][c] === STATE_INFRASTRUCTURE
                ? STATE_INFRASTRUCTURE_BURNT
                : STATE_EMPTY;
          }
        } else if (this.progress[r][c] > 0) {
          // Preheat dissipates once the fire front is no longer feeding it
          this.progress[r][c] *= Math.max(0, 1 - PROGRESS_DECAY_PER_HR * dt);
        }
      }
    }

    // Apply queued ignitions
    for (let i = 0; i < ignitions.length; i += 2) {
      const r = ignitions[i];
      const c = ignitions[i + 1];
      if (this.isCombustible(r, c)) this.igniteCell(r, c);
    }
  }

  // --- Firefighting tools ------------------------------------------------
  // Returns list of affected cells (for animations)
  applyTool(tool, row, col, weather) {
    const affected = [];
    const radius = TOOL_EFFECT_RADIUS[tool] || 0;

    if (tool === "RETARDANT_DROP") {
      const cells = this.retardantLineCells(row, col, weather?.direction ?? 0);
      for (const [r, c] of cells) {
        const s = this.state[r][c];
        if (s === STATE_TREE || s === STATE_INFRASTRUCTURE) {
          this.state[r][c] = STATE_RETARDANT;
          this.retardant.set(key(r, c), RETARDANT_DURATION_HRS);
          this.markers[key(r, c)] = "RETARDANT_DROP";
          this.digging.delete(key(r, c));
          affected.push([r, c]);
        }
      }
    } else if (tool === "BACKFIRE") {
      if (this.inBounds(row, col)) {
        const s = this.state[row][col];
        if (s === STATE_TREE || s === STATE_INFRASTRUCTURE) {
          this.igniteCell(row, col);
          affected.push([row, col]);
        }
      }
    } else {
      for (let dr = -radius; dr <= radius; dr++) {
        for (let dc = -radius; dc <= radius; dc++) {
          const r = row + dr;
          const c = col + dc;
          if (!this.inBounds(r, c)) continue;
          const s = this.state[r][c];
          const init = this.initialGridType[r][c];
          if (tool === "WATER_DROP") {
            if (s === STATE_BURNING) {
              this.state[r][c] = STATE_SMOLDERING;
              this.smolderRemaining[r][c] = 0.5;
              delete this.markers[key(r, c)];
              affected.push([r, c]);
            } else if (s === STATE_SMOLDERING) {
              this.state[r][c] =
                init === STATE_TREE || init === STATE_INFRASTRUCTURE
                  ? init
                  : STATE_EMPTY;
              this.progress[r][c] = 0;
              delete this.markers[key(r, c)];
              affected.push([r, c]);
            }
            // Dampen unburned fuel: knock accumulated arrival back
            this.progress[r][c] = Math.min(this.progress[r][c], 0.2);
          } else if (tool === "DIG_BREAK") {
            if (
              (s === STATE_TREE ||
                s === STATE_INFRASTRUCTURE ||
                s === STATE_EMPTY) &&
              !this.digging.has(key(r, c))
            ) {
              this.state[r][c] = STATE_DIGGING;
              this.digging.set(key(r, c), DIG_DURATION_HRS);
              this.markers[key(r, c)] = "DIG_BREAK";
              this.retardant.delete(key(r, c));
              affected.push([r, c]);
            }
          }
        }
      }
    }
    return affected;
  }

  // Retardant line perpendicular to the wind (blocks the downwind run)
  retardantLineCells(row, col, windFromDeg) {
    const towardRad = ((windFromDeg + 180) % 360) * (Math.PI / 180);
    // Perpendicular direction in grid space
    const px = Math.cos(towardRad); // perpendicular east comp
    const py = Math.sin(towardRad); // perpendicular south comp
    const dc = Math.abs(px) > 0.383 ? Math.sign(px) : 0;
    const dr = Math.abs(py) > 0.383 ? Math.sign(py) : 0;
    const cells = [];
    const start = -Math.floor((RETARDANT_LINE_LENGTH - 1) / 2);
    for (let i = 0; i < RETARDANT_LINE_LENGTH; i++) {
      const r = row + (start + i) * (dr || 0);
      const c = col + (start + i) * (dc === 0 && dr === 0 ? 1 : dc);
      if (this.inBounds(r, c)) cells.push([r, c]);
    }
    if (!cells.some(([r, c]) => r === row && c === col) && this.inBounds(row, col))
      cells.push([row, col]);
    return cells;
  }

  // --- Introspection ------------------------------------------------------
  stats() {
    let current = 0;
    let burnt = 0;
    let totalBurntTiles = 0;
    let valueLost = 0;
    let burning = 0;
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const s = this.state[r][c];
        const init = this.initialGridType[r][c];
        if (s === STATE_BURNING || s === STATE_SMOLDERING) burning++;
        if (s === STATE_INFRASTRUCTURE || (s === STATE_RETARDANT && init === STATE_INFRASTRUCTURE)) {
          current++;
        } else if (s === STATE_INFRASTRUCTURE_BURNT) {
          burnt++;
          totalBurntTiles++;
          valueLost += this.structureValue?.[r]?.[c] || 0;
        } else if (s === STATE_EMPTY && init !== STATE_WATER) {
          totalBurntTiles++;
        } else if (
          (s === STATE_BURNING || s === STATE_SMOLDERING) &&
          init === STATE_INFRASTRUCTURE
        ) {
          valueLost += this.structureValue?.[r]?.[c] || 0;
        }
      }
    }
    return {
      initial: this.initialInfraCount,
      current,
      burntInfrastructure: burnt,
      totalBurntTiles,
      valueLost,
      totalValue: this.totalValue,
      burning,
      moisture: this.moisture,
    };
  }

  collectCells(states) {
    const out = [];
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        if (states.includes(this.state[r][c])) out.push({ row: r, col: c });
      }
    }
    return out;
  }

  getRenderState() {
    return {
      grid: this.state,
      markers: { ...this.markers },
    };
  }
}
